import React, { useState, useEffect, useMemo } from 'react';
import { fetchDashboardClientStats, fetchDashboardClientTickets } from '@/features/dashboard/api';

export function DashboardClientSection({ userRole = '', currentUser = null }) {
  const role = String(userRole || currentUser?.Role || currentUser?.role || '').trim().toLowerCase();
  const isAuthorized = /^(super admin|admin|manager|pc)$/i.test(role);

  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortField, setSortField] = useState('total');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Drilldown modal state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientTickets, setClientTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState('');

  useEffect(() => {
    if (!isAuthorized) return;
    let alive = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchDashboardClientStats(controller.signal)
      .then((res) => {
        if (!alive) return;
        if (res.success && Array.isArray(res.stats)) {
          setStats(res.stats);
        } else {
          setStats([]);
        }
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.message || 'Failed to load client statistics');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [isAuthorized]);

  // Open modal and fetch client tickets
  const handleOpenClientDetails = (client) => {
    setSelectedClient(client);
    setTicketSearch('');
    setTicketsLoading(true);

    fetchDashboardClientTickets(client.clientId || client.clientName)
      .then((res) => {
        if (res.success && Array.isArray(res.tickets)) {
          setClientTickets(res.tickets);
        } else {
          setClientTickets([]);
        }
      })
      .catch(() => {
        setClientTickets([]);
      })
      .finally(() => {
        setTicketsLoading(false);
      });
  };

  const handleCloseModal = () => {
    setSelectedClient(null);
    setClientTickets([]);
  };

  // KPI aggregates
  const grandTotals = useMemo(() => {
    let totalTickets = 0;
    let pendingTickets = 0;
    let completeTickets = 0;
    let reworkTickets = 0;
    stats.forEach((c) => {
      totalTickets += Number(c.total || 0);
      pendingTickets += Number(c.pending || 0);
      completeTickets += Number(c.completed || 0);
      reworkTickets += Number(c.rework || 0);
    });
    return {
      totalClients: stats.length,
      totalTickets,
      pendingTickets,
      completeTickets,
      reworkTickets,
      closureRate: totalTickets ? Math.round((completeTickets / totalTickets) * 100) : 0
    };
  }, [stats]);

  // Filtered & sorted stats
  const filteredStats = useMemo(() => {
    let result = [...stats];

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (c) =>
          String(c.clientName || '').toLowerCase().includes(q) ||
          String(c.clientId || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'HAS_PENDING') {
      result = result.filter((c) => Number(c.pending || 0) > 0);
    } else if (statusFilter === 'HAS_REWORK') {
      result = result.filter((c) => Number(c.rework || 0) > 0);
    } else if (statusFilter === 'COMPLETED_100') {
      result = result.filter((c) => Number(c.total || 0) > 0 && c.completed === c.total);
    }

    result.sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      if (typeof valA === 'string') {
        const cmp = String(valA || '').localeCompare(String(valB || ''));
        return sortOrder === 'asc' ? cmp : -cmp;
      }
      return sortOrder === 'asc'
        ? (Number(valA) || 0) - (Number(valB) || 0)
        : (Number(valB) || 0) - (Number(valA) || 0);
    });

    return result;
  }, [stats, searchQuery, statusFilter, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredStats.length / pageSize));
  const pagedClients = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return filteredStats.slice(startIdx, startIdx + pageSize);
  }, [filteredStats, page]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const filteredClientTickets = useMemo(() => {
    if (!ticketSearch.trim()) return clientTickets;
    const q = ticketSearch.trim().toLowerCase();
    return clientTickets.filter(
      (t) =>
        String(t.id || '').toLowerCase().includes(q) ||
        String(t.description || '').toLowerCase().includes(q) ||
        String(t.employeeName || '').toLowerCase().includes(q) ||
        String(t.status || '').toLowerCase().includes(q)
    );
  }, [clientTickets, ticketSearch]);

  if (!isAuthorized) {
    return null;
  }

  return (
    <section className="dashboard-client-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          padding: '24px',
          boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* KPI Mini Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '14px',
            marginBottom: '20px'
          }}
        >
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>
              Total Clients
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginTop: '4px' }}>
              {grandTotals.totalClients}
            </div>
          </div>

          <div
            style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#2563eb', textTransform: 'uppercase' }}>
              Total Tickets
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#1e40af', marginTop: '4px' }}>
              {grandTotals.totalTickets}
            </div>
          </div>

          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#d97706', textTransform: 'uppercase' }}>
              Pending Tickets
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#b45309', marginTop: '4px' }}>
              {grandTotals.pendingTickets}
            </div>
          </div>

          <div
            style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#059669', textTransform: 'uppercase' }}>
              Completed Tickets
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#047857', marginTop: '4px' }}>
              {grandTotals.completeTickets}
            </div>
          </div>

          <div
            style={{
              background: '#fff1f2',
              border: '1px solid #fecdd3',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#e11d48', textTransform: 'uppercase' }}>
              Rework / Reassigned
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#be123c', marginTop: '4px' }}>
              {grandTotals.reworkTickets}
            </div>
          </div>

          <div
            style={{
              background: '#faf5ff',
              border: '1px solid #e9d5ff',
              borderRadius: '12px',
              padding: '14px 16px'
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#9333ea', textTransform: 'uppercase' }}>
              Closure Rate
            </div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#7e22ce', marginTop: '4px' }}>
              {grandTotals.closureRate}%
            </div>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            background: '#f8fafc',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}
        >
          {/* Search Input */}
          <div style={{ position: 'relative', flex: '1', minWidth: '240px', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Search by client name or ID..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none',
                background: '#fff'
              }}
            />
            <span
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                fontSize: '14px',
                pointerEvents: 'none'
              }}
            >
              🔍
            </span>
          </div>

          {/* Quick Filter Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {[
              { id: 'ALL', label: 'All Clients' },
              { id: 'HAS_PENDING', label: '⚠️ Has Pending' },
              { id: 'HAS_REWORK', label: '🔄 Has Rework' },
              { id: 'COMPLETED_100', label: '✅ 100% Closed' }
            ].map((f) => {
              const active = statusFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setStatusFilter(f.id);
                    setPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: active ? '700' : '500',
                    border: active ? '1px solid #2563eb' : '1px solid #cbd5e1',
                    background: active ? '#2563eb' : '#fff',
                    color: active ? '#fff' : '#475569',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Loading / Error States */}
        {loading ? (
          <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
            Loading client ticket statistics...
          </div>
        ) : error ? (
          <div
            style={{
              padding: '16px',
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '13px'
            }}
          >
            {error}
          </div>
        ) : filteredStats.length === 0 ? (
          <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏢</div>
            No clients match the current search or filter.
          </div>
        ) : (
          <>
            {/* Clients Table */}
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 14px', width: '40px', textAlign: 'center', color: '#475569' }}>#</th>
                    <th
                      onClick={() => handleSort('clientId')}
                      style={{ padding: '12px 14px', cursor: 'pointer', color: '#475569' }}
                    >
                      Client ID {sortField === 'clientId' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('clientName')}
                      style={{ padding: '12px 14px', cursor: 'pointer', color: '#475569' }}
                    >
                      Client / Project Name {sortField === 'clientName' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('total')}
                      style={{ padding: '12px 14px', textAlign: 'center', cursor: 'pointer', color: '#475569' }}
                    >
                      Total Tickets {sortField === 'total' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('pending')}
                      style={{ padding: '12px 14px', textAlign: 'center', cursor: 'pointer', color: '#d97706' }}
                    >
                      Pending {sortField === 'pending' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('completed')}
                      style={{ padding: '12px 14px', textAlign: 'center', cursor: 'pointer', color: '#059669' }}
                    >
                      Completed {sortField === 'completed' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('rework')}
                      style={{ padding: '12px 14px', textAlign: 'center', cursor: 'pointer', color: '#e11d48' }}
                    >
                      Rework {sortField === 'rework' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      onClick={() => handleSort('closureRate')}
                      style={{ padding: '12px 14px', width: '130px', cursor: 'pointer', color: '#475569' }}
                    >
                      Closure Rate {sortField === 'closureRate' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ padding: '12px 14px', textAlign: 'center', width: '110px', color: '#475569' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedClients.map((c, idx) => {
                    const rowNumber = (page - 1) * pageSize + idx + 1;
                    const closurePct = Number(c.closureRate || 0);
                    const hasPending = Number(c.pending || 0) > 0;
                    const hasRework = Number(c.rework || 0) > 0;

                    return (
                      <tr
                        key={c.clientId || c.clientName || idx}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <td style={{ padding: '12px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                          {rowNumber}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontWeight: '700',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '11px'
                            }}
                          >
                            {c.clientId || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#0f172a' }}>
                          {c.clientName || 'Unassigned'}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span
                            style={{
                              background: '#f1f5f9',
                              color: '#1e293b',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontWeight: '700'
                            }}
                          >
                            {c.total}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span
                            style={{
                              background: hasPending ? '#fef3c7' : '#f8fafc',
                              color: hasPending ? '#b45309' : '#94a3b8',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontWeight: hasPending ? '700' : '500'
                            }}
                          >
                            {c.pending}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span
                            style={{
                              background: Number(c.completed || 0) > 0 ? '#dcfce7' : '#f8fafc',
                              color: Number(c.completed || 0) > 0 ? '#15803d' : '#94a3b8',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontWeight: Number(c.completed || 0) > 0 ? '700' : '500'
                            }}
                          >
                            {c.completed}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span
                            style={{
                              background: hasRework ? '#fee2e2' : '#f8fafc',
                              color: hasRework ? '#b91c1c' : '#94a3b8',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontWeight: hasRework ? '700' : '500'
                            }}
                          >
                            {c.rework}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                flex: 1,
                                height: '7px',
                                background: '#e2e8f0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}
                            >
                              <div
                                style={{
                                  width: `${closurePct}%`,
                                  height: '100%',
                                  background:
                                    closurePct === 100
                                      ? '#10b981'
                                      : closurePct > 50
                                      ? '#3b82f6'
                                      : '#f59e0b',
                                  borderRadius: '4px'
                                }}
                              />
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569', minWidth: '32px' }}>
                              {closurePct}%
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenClientDetails(c)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              background: '#fff',
                              color: '#2563eb',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            View Tickets ↗
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '14px',
                flexWrap: 'wrap',
                gap: '10px',
                fontSize: '12px',
                color: '#64748b'
              }}
            >
              <div>
                Showing <b>{(page - 1) * pageSize + 1}</b> to{' '}
                <b>{Math.min(page * pageSize, filteredStats.length)}</b> of <b>{filteredStats.length}</b> clients
              </div>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: page <= 1 ? '#f1f5f9' : '#fff',
                    color: page <= 1 ? '#94a3b8' : '#334155',
                    cursor: page <= 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ← Prev
                </button>
                <span style={{ padding: '0 6px', fontWeight: '600' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: page >= totalPages ? '#f1f5f9' : '#fff',
                    color: page >= totalPages ? '#94a3b8' : '#334155',
                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Drill-down Modal for Client Tickets */}
      {selectedClient ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: '700',
                      background: '#e0e7ff',
                      color: '#3730a3',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  >
                    {selectedClient.clientId || '-'}
                  </span>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>
                    {selectedClient.clientName || 'Client Tickets'}
                  </h3>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '11px' }}>
                  <span style={{ color: '#64748b' }}>
                    Total: <b>{selectedClient.total}</b>
                  </span>
                  <span style={{ color: '#b45309' }}>
                    Pending: <b>{selectedClient.pending}</b>
                  </span>
                  <span style={{ color: '#15803d' }}>
                    Completed: <b>{selectedClient.completed}</b>
                  </span>
                  <span style={{ color: '#b91c1c' }}>
                    Rework: <b>{selectedClient.rework}</b>
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body & Ticket Filter */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
              <input
                type="text"
                placeholder="Filter tickets by ID, description, assignee, status..."
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '7px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              {ticketsLoading ? (
                <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                  Loading client tickets...
                </div>
              ) : filteredClientTickets.length === 0 ? (
                <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                  No tickets found for this client.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Ticket ID</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Date</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Description</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Assignee</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Priority</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>TAT</th>
                      <th style={{ padding: '8px 10px', color: '#475569' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClientTickets.map((t, i) => {
                      const st = String(t.status || '').toLowerCase();
                      const isDone = /closed|completed|done|resolved|approved/i.test(st);
                      const isPendingAppr = /pending approval|approval/i.test(st);
                      const isRew = /rework|reassigned/i.test(st);

                      let pillBg = '#f1f5f9';
                      let pillColor = '#475569';
                      if (isDone) {
                        pillBg = '#dcfce7';
                        pillColor = '#15803d';
                      } else if (isPendingAppr) {
                        pillBg = '#fef3c7';
                        pillColor = '#b45309';
                      } else if (isRew) {
                        pillBg = '#fee2e2';
                        pillColor = '#b91c1c';
                      } else if (/in progress|paused/i.test(st)) {
                        pillBg = '#e0f2fe';
                        pillColor = '#0369a1';
                      } else {
                        pillBg = '#fef3c7';
                        pillColor = '#d97706';
                      }

                      return (
                        <tr key={t.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 10px', fontWeight: '700', fontFamily: 'monospace' }}>
                            {t.id}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#64748b' }}>{t.date || '-'}</td>
                          <td style={{ padding: '8px 10px', maxWidth: '280px', color: '#1e293b' }}>
                            {t.description}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#334155', fontWeight: '500' }}>
                            {t.employeeName}
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            <span
                              style={{
                                color: /high|urgent/i.test(t.priority) ? '#dc2626' : '#64748b',
                                fontWeight: /high|urgent/i.test(t.priority) ? '700' : '500'
                              }}
                            >
                              {t.priority || 'Normal'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', color: '#64748b' }}>{t.duration || '-'}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span
                              style={{
                                background: pillBg,
                                color: pillColor,
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}
                            >
                              {t.status || 'Open'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  padding: '6px 16px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
